import { useAssetId } from "@/setup/hooks/useAssetId";
import { OperatorSelector } from "@/shared/selectors/OperatorSelector";
import { ErrorModal, getMomentTz, MesfModal } from "@dexteel/mesf-core";
import { Button, CircularProgress, Grid2 } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { upsertPartReset } from "../../repositories/PartsReportRepository";

const moment = getMomentTz();

export const ResetPartCampaignModal = ({
	open,
	onHide,
	partId,
	info = { partName: "", partType: "", campaignWeight: 0 },
}: {
	open: boolean;
	onHide: () => void;
	partId: number;
	info: {
		partName?: string | null;
		partType?: string | null;
		campaignWeight?: number;
	};
}) => {
	const assetId = useAssetId();
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");

	const { control, reset, getValues } = useForm({
		defaultValues: { operatorId: 0, resetAt: new Date() },
		mode: "onChange",
	});

	const queryClient = useQueryClient();
	const updatePartReset = useMutation(upsertPartReset, {
		meta: { successMessage: "Part campaign reset successfully" },
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["parts", partId],
			});
			await queryClient.invalidateQueries({
				queryKey: ["partCampaignData", partId],
			});
			onHide();
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	const handleSubmit = () => {
		setIsSubmitLoading(true);
		updatePartReset.mutate({
			partResetId: null,
			partId: partId!,
			resetAt: getValues("resetAt"),
			resetBy: getValues("operatorId"),
			comments: null,
		});
	};

	useEffect(() => {
		reset({
			operatorId: 0,
			resetAt: new Date(),
		});
	}, [open]);

	return (
		<>
			<MesfModal
				title="CONFIRM RESET COUNTER"
				open={open}
				id="reset-campaign-modal"
				maxWidth="sm"
				handleClose={onHide}
			>
				<MesfModal.Content style={{ padding: "15px 30px" }} dividers>
					<Grid2 container spacing={2}>
						<Grid2 size={{ xs: 12 }}>
							<p>
								Are you sure that you want to reset counter for the{" "}
								{info.partType}: <strong>{info.partName}</strong>
							</p>
						</Grid2>
						<Grid2 size={{ xs: 12 }}>
							<Controller
								name="resetAt"
								control={control}
								shouldUnregister
								rules={{
									required: "Reset timestamp is required",
								}}
								render={({ field, fieldState: { error } }) => (
									<DateTimePicker
										label="Timestamp"
										value={field.value ? moment(field.value) : null}
										onChange={field.onChange}
										ampm={false}
										slotProps={{
											textField: {
												fullWidth: true,
												required: true,
												error: !!error,
												helperText: error?.message,
											},
										}}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12 }}>
							<Controller
								name="operatorId"
								control={control}
								shouldUnregister
								rules={{
									required: "Operator is required",
								}}
								render={({ field, fieldState: { error } }) => (
									<OperatorSelector
										assetId={assetId}
										value={field.value}
										onChange={field.onChange}
										label="Operator"
										fieldError={error}
										required
									/>
								)}
							/>
						</Grid2>
					</Grid2>
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
					<Grid2 container spacing={2} justifyContent="flex-end">
						<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={onHide}
								disabled={isSubmitLoading}
							>
								Cancel
							</Button>
						</Grid2>
						<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
							<Button
								fullWidth
								onClick={handleSubmit}
								startIcon={
									isSubmitLoading ? (
										<CircularProgress size="1rem" color="inherit" />
									) : null
								}
								variant="contained"
								color="primary"
								disabled={isSubmitLoading}
							>
								Reset
							</Button>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
